import toast from "react-hot-toast";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

export type ApiErrorResponse = {
  trigger: string;
  message: string;
};

export type ApiResponse<T> = {
  res: T | null;
  error: AxiosError | null;
};

export async function handleApi<T>(
  promise: Promise<T>,
  showToast: boolean = true
): Promise<ApiResponse<T>> {
  try {
    const res = await promise;
    return { res, error: null };
  } catch (error) {
    const axiosError = error as AxiosError;

    if (axiosError.code === "ERR_CANCELED") {
      console.warn("Request canceled");
      return { res: null, error: null };
    }

    const data = axiosError.response?.data;
    const hasTrigger = data && typeof data === "object" && "trigger" in data;
    
    if (!hasTrigger) {
      const endpoint = axiosError.config?.url || "unknown-endpoint";
      const method = axiosError.config?.method?.toUpperCase() || "GET";
      const status = axiosError.response?.status || "no-response";
      const statusNum = typeof status === "number" ? status : 0;
      
      const errorTitle = `API Error: ${method} ${endpoint} (${status})`;
      const descriptiveError = new Error(errorTitle);
      (descriptiveError as any).originalError = axiosError;
      
      Sentry.captureException(descriptiveError, {
        tags: {
          http_status: status,
          http_method: method,
          endpoint: endpoint,
          error_type: "uncontrolled_api_error",
        },
        contexts: {
          request: {
            url: axiosError.config?.url,
            method: method,
            headers: axiosError.config?.headers,
            params: axiosError.config?.params,
          },
          response: {
            status: status,
            statusText: axiosError.response?.statusText,
            data: axiosError.response?.data,
          },
          original_error: {
            name: axiosError.name,
            message: axiosError.message,
            code: axiosError.code,
          },
        },
        fingerprint: [method, endpoint, String(status)],
        level: statusNum >= 500 ? "error" : "warning",
      });
      console.error("Error: ", errorTitle);
    }

    const errorMessage = handleApiErrorMessages(axiosError);
    if (showToast) toast.error(errorMessage);
    return {
      res: null,
      error: error as AxiosError,
    };
  }
}

export const handleApiErrorMessages = (error: AxiosError): string => {
  const defaultMessage = "An unexpected error has occurred";

  const data = error.response?.data;

  if (typeof data === "object" && data !== null) {
    const { message, trigger } = data as ApiErrorResponse;
    return message || trigger || defaultMessage;
  }

  if (typeof data === "string") return data;

  return error.message || defaultMessage;
};
